import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, AlertTriangle, Sparkles, ImagePlus, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { MOODS, CATEGORIES } from "@/lib/constants";
import { api } from "@/lib/api";
import MoodBadge from "@/components/MoodBadge";

const MAX = 2000;
const MAX_BYTES = 8 * 1024 * 1024;

export default function PostComposer({ user, onPosted }) {
  const [content, setContent] = useState("");
  const [mood, setMood] = useState("furious");
  const [category, setCategory] = useState("Life");
  const [tw, setTw] = useState(false);
  const [posting, setPosting] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [imagePath, setImagePath] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const chooseFile = () => fileRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_BYTES) {
      toast.error("Image too large (max 8MB).");
      return;
    }
    setUploading(true);
    const localUrl = URL.createObjectURL(f);
    setImagePreview(localUrl);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post(`/upload?user_id=${user.id}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImagePath(data.path);
      toast.success("Photo attached.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
      setImagePreview(null);
      URL.revokeObjectURL(localUrl);
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setImagePath(null);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
  };

  const submit = async () => {
    if (!content.trim() && !imagePath) {
      toast.error("Nothing to release yet.");
      return;
    }
    setPosting(true);
    try {
      const { data } = await api.post("/posts", {
        user_id: user.id,
        content,
        mood,
        category,
        trigger_warning: tw,
        image_path: imagePath,
      });
      setPulse(true);
      setTimeout(() => setPulse(false), 900);
      setContent("");
      setTw(false);
      removeImage();
      toast.success("Vent released into the void.");
      onPosted?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something broke. Try again.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div
      className={`rounded-2xl border p-5 sm:p-6 ${pulse ? "exhale-release" : ""}`}
      style={{ background: "var(--exhale-surface)", borderColor: "var(--exhale-border)" }}
      data-testid="post-composer"
    >
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4" style={{ color: "var(--exhale-accent)" }} />
        <h3 className="font-display text-lg font-semibold" style={{ color: "var(--exhale-text)" }}>
          Let it out, {user.handle}
        </h3>
      </div>
      <Textarea
        data-testid="composer-textarea"
        value={content}
        onChange={(e) => setContent(e.target.value.slice(0, MAX))}
        placeholder="Type whatever the hell you need to scream into the void. No filters, no judgment."
        rows={5}
        className="resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base"
        style={{ background: "var(--exhale-bg)", color: "var(--exhale-text)" }}
      />

      {imagePreview && (
        <div className="mt-3 relative inline-block" data-testid="composer-image-preview">
          <img
            src={imagePreview}
            alt="attached"
            className="max-h-64 rounded-xl border"
            style={{ borderColor: "var(--exhale-border)" }}
          />
          <button
            type="button"
            onClick={removeImage}
            data-testid="composer-remove-image"
            className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center"
            style={{ background: "rgba(12,11,14,0.85)", border: "1px solid var(--exhale-border)", color: "var(--exhale-text)" }}
          >
            <X className="w-4 h-4" />
          </button>
          {uploading && (
            <div
              className="absolute inset-0 rounded-xl flex items-center justify-center text-xs font-mono uppercase"
              style={{ background: "rgba(12,11,14,0.65)", color: "var(--exhale-accent-2)" }}
            >
              uploading…
            </div>
          )}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-xs font-mono" style={{ color: "var(--exhale-text-muted)" }}>
            {content.length}/{MAX}
          </span>
          <button
            type="button"
            onClick={chooseFile}
            disabled={uploading || !!imagePath}
            data-testid="composer-attach-image"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-all disabled:opacity-50"
            style={{ background: "var(--exhale-badge)", border: "1px solid var(--exhale-border)", color: "var(--exhale-accent-2)" }}
          >
            <ImagePlus className="w-3.5 h-3.5" />
            {imagePath ? "Photo attached" : uploading ? "Uploading…" : "Add photo"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onFile}
            className="hidden"
            data-testid="composer-file-input"
          />
        </div>
        <label className="flex items-center gap-2 text-xs" style={{ color: "var(--exhale-text-2)" }} data-testid="trigger-warning-toggle-wrap">
          <AlertTriangle className="w-3.5 h-3.5" />
          Trigger warning
          <Switch checked={tw} onCheckedChange={setTw} data-testid="trigger-warning-switch" />
        </label>
      </div>

      <div className="mt-4">
        <div className="text-xs font-medium tracking-wider uppercase mb-2" style={{ color: "var(--exhale-text-muted)" }}>
          Mood
        </div>
        <div className="flex flex-wrap gap-2">
          {MOODS.map((m) => {
            const active = mood === m.id;
            return (
              <button
                key={m.id}
                onClick={() => setMood(m.id)}
                data-testid={`mood-selector-${m.id}`}
                className="transition-transform hover:scale-105 focus:outline-none"
                style={{ opacity: active ? 1 : 0.55, transform: active ? "scale(1.03)" : "scale(1)" }}
              >
                <MoodBadge moodId={m.id} size="lg" />
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4">
        <div className="text-xs font-medium tracking-wider uppercase mb-2" style={{ color: "var(--exhale-text-muted)" }}>
          Category
        </div>
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => {
            const active = category === c;
            return (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`category-selector-${c.toLowerCase()}`}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                style={{
                  background: active ? "var(--exhale-accent)" : "var(--exhale-badge)",
                  color: active ? "#0C0B0E" : "var(--exhale-text-2)",
                  border: `1px solid ${active ? "var(--exhale-accent)" : "var(--exhale-border)"}`,
                }}
              >
                {c}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button
          onClick={submit}
          disabled={posting || uploading || (!content.trim() && !imagePath)}
          data-testid="post-vent-button"
          className="rounded-full px-5 h-11 font-semibold ember-glow"
          style={{ background: "var(--exhale-accent)", color: "#0C0B0E" }}
        >
          <Send className="w-4 h-4 mr-2" />
          {posting ? "Releasing…" : "Scream into the void"}
        </Button>
      </div>
    </div>
  );
}

// duplicate imageUrl helper removed — use imageUrl from '@/lib/api' if needed
